import React, { useState, useEffect } from "react";
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  CssBaseline,
  useTheme,
  useMediaQuery,
  Avatar,
  Menu,
  MenuItem,
  Tooltip,
  Button,
  alpha,
  Badge,
  BottomNavigation,
  BottomNavigationAction,
  Paper,
  Chip,
} from "@mui/material";
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  Receipt as ReceiptIcon,
  Description as DescriptionIcon,
  Group as GroupIcon,
  AccountBalance as AccountBalanceIcon,
  Settings as SettingsIcon,
  Logout as LogoutIcon,
  CloudUpload as CloudUploadIcon,
  Folder as FolderIcon,
  Work as WorkIcon,
  NotificationsNone as NotificationsIcon,
  Percent as PercentIcon,
  BusinessCenter as BusinessCenterIcon,
  Assessment as AssessmentIcon,
  FactCheck as FactCheckIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Add as AddIcon,
} from "@mui/icons-material";
import {
  useNavigate,
  useLocation,
  useSearchParams,
  Outlet,
} from "react-router-dom";
import { signOut } from "firebase/auth";
import { auth } from "../../firebase";
import { motion, AnimatePresence } from "framer-motion";
import ThemeToggle from "../common/ThemeToggle";
import { tokens } from "../../theme";
import api from "../../services/api";
import { useTrial } from "../../contexts/TrialContext";
import Logo from "../common/Logo";
import PaymentFailedModal from "../PaymentFailedModal";

const drawerWidth = 260;

// Flat navigation with section labels
const navigationConfig = [
  { section: "DASHBOARD" },
  {
    id: "home",
    text: "Home",
    icon: <DashboardIcon />,
    path: "/dashboard",
    exact: true,
  },
  { section: "INCOME" },
  {
    id: "invoices",
    text: "Invoices",
    icon: <DescriptionIcon />,
    path: "/dashboard/invoices",
  },
  {
    id: "clients",
    text: "Clients",
    icon: <GroupIcon />,
    path: "/dashboard/clients",
  },
  {
    id: "projects",
    text: "Projects",
    icon: <FolderIcon />,
    path: "/dashboard/projects",
  },
  { section: "EXPENSES" },
  {
    id: "receipts",
    text: "Receipts",
    icon: <ReceiptIcon />,
    path: "/dashboard/inbox",
  },
  {
    id: "bank-feed",
    text: "Bank Feed",
    icon: <AccountBalanceIcon />,
    path: "/dashboard/bank-transactions",
  },
  { section: "TAX" },
  {
    id: "vat",
    text: "VAT Returns",
    icon: <PercentIcon />,
    path: "/dashboard/vat",
  },
  {
    id: "payroll",
    text: "Payroll",
    icon: <WorkIcon />,
    path: "/dashboard/payroll",
  },
  {
    id: "director",
    text: "Director",
    icon: <BusinessCenterIcon />,
    path: "/dashboard/director",
  },
  { section: "REPORTS" },
  {
    id: "accounts",
    text: "Accounts",
    icon: <AssessmentIcon />,
    path: "/dashboard/accounts",
  },
  {
    id: "year-end",
    text: "Year-End Review",
    icon: <FactCheckIcon />,
    path: "/dashboard/review",
  },
];

const bottomNavItems = [
  { id: "home", label: "Home", icon: <DashboardIcon />, path: "/dashboard" },
  {
    id: "money-in",
    label: "Income",
    icon: <TrendingUpIcon />,
    path: "/dashboard/invoices",
  },
  {
    id: "upload",
    label: "Upload",
    icon: <AddIcon />,
    path: "/dashboard/upload",
    primary: true,
  },
  {
    id: "money-out",
    label: "Expenses",
    icon: <TrendingDownIcon />,
    path: "/dashboard/inbox",
  },
  { id: "more", label: "More", icon: <MenuIcon />, action: "menu" },
];

const secondaryItems = [
  { text: "Settings", icon: <SettingsIcon />, path: "/dashboard/settings" },
];

export default function DashboardLayout({ user, children }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [open, setOpen] = useState(!isMobile);
  const [anchorEl, setAnchorEl] = useState(null);
  const [bottomNavValue, setBottomNavValue] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();
  const {
    plan,
    isTrialExpired,
    trialDaysLeft,
    isPro,
    setPlan,
    setIsPro,
    setIsTrialExpired,
    subscriptionStatus,
  } = useTrial();

  const isPaymentFailed = subscriptionStatus === "past_due";
  const isSubCanceled = subscriptionStatus === "canceled" && !isPro;
  const [upgrading, setUpgrading] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  // After Stripe redirect, verify the session and update Firestore + local state
  useEffect(() => {
    const sessionId = searchParams.get("session_id");
    if (!sessionId) return;

    const verifyPayment = async () => {
      try {
        const res = await api.get(
          `/stripe/verify-session?session_id=${sessionId}`,
        );
        if (res.data?.success) {
          console.log("✅ Payment verified, upgrading to Pro");
          setPlan("pro");
          setIsPro(true);
          setIsTrialExpired(false);
        }
      } catch (err) {
        console.error("❌ Payment verification failed:", err);
      } finally {
        // Clean up URL params
        searchParams.delete("session_id");
        searchParams.delete("source");
        setSearchParams(searchParams, { replace: true });
      }
    };

    verifyPayment();
  }, []);

  const handleUpgrade = async () => {
    setUpgrading(true);
    const source = isTrialExpired ? "header_expired" : "header_active";
    try {
      if (!auth.currentUser) return;
      const token = await auth.currentUser.getIdToken();
      const res = await api.post(
        "/stripe/create-checkout-session",
        {
          uid: auth.currentUser.uid,
          email: auth.currentUser.email,
          source,
          planType: "pro",
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      window.location.href = res.data.url;
    } catch (err) {
      console.error("Upgrade error:", err);
      // Ideally show a snackbar here, but for now log it
      setUpgrading(false);
    }
  };

  const handleDrawerToggle = () => {
    setOpen(!open);
  };
  // ... (rest of component code)
  {
    !isPro && (
      <Button
        variant="contained"
        color={isTrialExpired ? "error" : "primary"}
        size="small"
        onClick={handleUpgrade}
        disabled={upgrading}
        sx={{
          mr: 2,
          fontWeight: 600,
          boxShadow: "none",
          textTransform: "none",
        }}
      >
        {upgrading ? "Loading..." : "Upgrade"}
      </Button>
    );
  }

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    await signOut(auth);
    navigate("/login");
  };

  const handleBottomNavChange = (event, newValue) => {
    const item = bottomNavItems[newValue];
    if (item.action === "menu") {
      setOpen(true);
    } else if (item.path) {
      navigate(item.path);
    }
    setBottomNavValue(newValue);
  };

  const drawerContent = (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        bgcolor: "background.paper",
      }}
    >
      <Toolbar
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 3,
          pt: 1,
          minHeight: 80,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <motion.div
            whileHover={{ scale: 1.05, rotate: 5 }}
            whileTap={{ scale: 0.95 }}
          >
            <Logo sx={{ width: 40, height: 40, borderRadius: "50%" }} />
          </motion.div>
          <Typography
            variant="h6"
            sx={{ fontWeight: 800, color: "text.primary", letterSpacing: -0.5 }}
          >
            SortlyBooks
          </Typography>
        </Box>
        <ThemeToggle size="small" />
      </Toolbar>

      <Box sx={{ px: 2, py: 2 }}>
        <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
          <Button
            fullWidth
            variant="contained"
            color="primary"
            startIcon={<CloudUploadIcon />}
            onClick={() => {
              navigate("/dashboard/upload");
              if (isMobile) setOpen(false);
            }}
            sx={{
              py: 1.3,
              fontSize: "0.95rem",
              boxShadow: `0 10px 20px -5px ${alpha(
                theme.palette.primary.main,
                0.4,
              )}`,
            }}
          >
            Upload Receipt
          </Button>
        </motion.div>
      </Box>

      <List sx={{ px: 2, flexGrow: 1 }}>
        {navigationConfig.map((item, idx) => {
          // Section label
          if (item.section) {
            return (
              <Typography
                key={item.section}
                variant="overline"
                sx={{
                  px: 2,
                  mt: idx === 0 ? 0 : 1.5,
                  mb: 0.5,
                  display: "block",
                  color: "text.disabled",
                  fontWeight: 700,
                  fontSize: "0.65rem",
                  letterSpacing: 1.2,
                }}
              >
                {item.section}
              </Typography>
            );
          }

          // Nav item
          const isActive = item.exact
            ? location.pathname === item.path
            : location.pathname.startsWith(item.path);

          return (
            <ListItem key={item.id} disablePadding sx={{ mb: 0.25 }}>
              <ListItemButton
                onClick={() => {
                  navigate(item.path);
                  if (isMobile) setOpen(false);
                }}
                selected={isActive}
                sx={{
                  borderRadius: 2.5,
                  py: 0.9,
                  px: 2,
                  color: isActive ? "primary.main" : "text.secondary",
                  bgcolor: isActive
                    ? alpha(theme.palette.primary.main, 0.08)
                    : "transparent",
                  transition: `all ${tokens.animation.durationFast} ${tokens.animation.easeInOut}`,
                  "&:hover": {
                    bgcolor: alpha(theme.palette.primary.main, 0.06),
                    color: "primary.main",
                    transform: "translateX(3px)",
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 36,
                    color: isActive ? "primary.main" : "text.secondary",
                    "& .MuiSvgIcon-root": { fontSize: "1.2rem" },
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                <ListItemText
                  primary={item.text}
                  primaryTypographyProps={{
                    fontWeight: isActive ? 700 : 500,
                    fontSize: "0.875rem",
                  }}
                />
                {item.pro && (
                  <Chip
                    label="PRO"
                    size="small"
                    sx={{
                      height: 18,
                      fontSize: "0.6rem",
                      fontWeight: 800,
                      bgcolor: alpha(theme.palette.primary.main, 0.1),
                      color: "primary.main",
                      "& .MuiChip-label": { px: 0.8 },
                    }}
                  />
                )}
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>

      <Divider sx={{ mx: 3, my: 1, borderColor: "divider" }} />

      <List sx={{ px: 2, pb: 3 }}>
        {secondaryItems.map((item) => {
          const isActive = location.pathname === item.path;
          return (
            <ListItem key={item.text} disablePadding>
              <ListItemButton
                onClick={() => {
                  navigate(item.path);
                  if (isMobile) setOpen(false);
                }}
                selected={isActive}
                sx={{
                  borderRadius: 3,
                  py: 1.2,
                  px: 2,
                  color: isActive ? "primary.main" : "text.secondary",
                  bgcolor: isActive
                    ? alpha(theme.palette.primary.main, 0.08)
                    : "transparent",
                  transition: `all ${tokens.animation.durationFast} ${tokens.animation.easeInOut}`,
                  "&:hover": {
                    color: "primary.main",
                    bgcolor: alpha(theme.palette.primary.main, 0.06),
                    transform: "translateX(4px)",
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 40,
                    color: isActive ? "primary.main" : "text.secondary",
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                <ListItemText
                  primary={item.text}
                  primaryTypographyProps={{
                    fontWeight: 500,
                    fontSize: "0.95rem",
                  }}
                />
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>
    </Box>
  );

  return (
    <Box
      sx={{
        display: "flex",
        minHeight: "100vh",
        bgcolor: "background.default",
      }}
    >
      <CssBaseline />

      {/* Payment failure modal — fires on every fresh login session until resolved */}
      <PaymentFailedModal
        subscriptionStatus={subscriptionStatus}
        isPro={isPro}
      />

      {/* Modern Header */}
      <AppBar
        position="fixed"
        sx={{
          width: { md: `calc(100% - ${drawerWidth}px)` },
          ml: { md: `${drawerWidth}px` },
          boxShadow: "none",
          bgcolor: "background.default", // Transparent/blends with background
          borderBottom: "1px solid", // Optional: remove if you want cleaner look
          borderColor: "rgba(0,0,0,0.03)",
          backdropFilter: "blur(8px)",
          color: "text.primary",
          zIndex: (theme) => theme.zIndex.drawer + 1,
        }}
      >
        <Toolbar sx={{ justifyContent: "space-between", height: 80 }}>
          <IconButton
            color="inherit"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { md: "none" } }}
          >
            <MenuIcon />
          </IconButton>

          <Box>
            {/* Breadcrumbs or Page Title could go here */}
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                letterSpacing: -0.5,
                display: { xs: "none", sm: "block" },
              }}
            >
              Dashboard
            </Typography>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {/* Plan Status Badge */}
            <Box
              sx={{
                mr: 2,
                px: 1.5,
                py: 0.5,
                borderRadius: 2,
                fontSize: "0.85rem",
                fontWeight: 700,
                bgcolor: isPaymentFailed || isSubCanceled
                  ? "#fff7ed"
                  : isPro
                  ? "#dcfce7"
                  : "#ffedd5",
                color: isPaymentFailed || isSubCanceled
                  ? "#c2410c"
                  : isPro
                  ? "#166534"
                  : "#9a3412",
                border: "1px solid",
                borderColor: isPaymentFailed || isSubCanceled
                  ? "#fed7aa"
                  : isPro
                  ? "#bbf7d0"
                  : "#fed7aa",
                display: { xs: "none", sm: "block" },
              }}
            >
              {isPaymentFailed
                ? "⚠️ Payment Failed"
                : isSubCanceled
                ? "⚠️ Subscription Ended"
                : isPro
                ? "Pro Plan"
                : isTrialExpired
                ? "Trial Expired"
                : `Trial: ${trialDaysLeft ?? 0} days left`}
            </Box>

            {!isPro && (
              <Button
                variant="contained"
                color={isTrialExpired ? "error" : "primary"}
                size="small"
                onClick={handleUpgrade}
                disabled={upgrading}
                sx={{
                  mr: 2,
                  fontWeight: 600,
                  boxShadow: "none",
                  textTransform: "none",
                }}
              >
                {upgrading ? "Loading..." : "Upgrade"}
              </Button>
            )}

            <Tooltip title="Notifications">
              <IconButton sx={{ color: "text.secondary" }}>
                <NotificationsIcon />
              </IconButton>
            </Tooltip>

            <Tooltip title="Account settings">
              <IconButton
                onClick={handleMenuOpen}
                sx={{
                  ml: 1,
                  p: 0.5,
                  border: "1px solid",
                  borderColor: "divider",
                }}
              >
                <Avatar
                  sx={{
                    width: 36,
                    height: 36,
                    bgcolor: "primary.main",
                    color: "white",
                    fontSize: "0.9rem",
                    fontWeight: 700,
                  }}
                >
                  {user?.email?.charAt(0).toUpperCase()}
                </Avatar>
              </IconButton>
            </Tooltip>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
              onClick={handleMenuClose}
              PaperProps={{
                elevation: 0,
                sx: {
                  overflow: "visible",
                  filter: "drop-shadow(0px 10px 30px rgba(0,0,0,0.1))",
                  mt: 1.5,
                  minWidth: 200,
                  "& .MuiAvatar-root": {
                    width: 32,
                    height: 32,
                    ml: -0.5,
                    mr: 1,
                  },
                },
              }}
              transformOrigin={{ horizontal: "right", vertical: "top" }}
              anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
            >
              <Box sx={{ px: 2, py: 1.5 }}>
                <Typography variant="subtitle2" noWrap sx={{ fontWeight: 600 }}>
                  {user?.displayName || "User"}
                </Typography>
                <Typography variant="caption" color="text.secondary" noWrap>
                  {user?.email}
                </Typography>
              </Box>
              <Divider />
              <MenuItem
                onClick={() => navigate("/dashboard/settings")}
                sx={{ py: 1.5 }}
              >
                <ListItemIcon>
                  <SettingsIcon fontSize="small" />
                </ListItemIcon>
                Settings
              </MenuItem>
              <MenuItem
                onClick={handleLogout}
                sx={{ py: 1.5, color: "error.main" }}
              >
                <ListItemIcon sx={{ color: "error.main" }}>
                  <LogoutIcon fontSize="small" />
                </ListItemIcon>
                Logout
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>

      <Drawer
        variant={isMobile ? "temporary" : "permanent"}
        open={open}
        onClose={isMobile ? handleDrawerToggle : undefined}
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: drawerWidth,
            boxSizing: "border-box",
            borderRight: "1px solid",
            borderColor: "divider",
            bgcolor: "background.paper",
          },
        }}
      >
        {drawerContent}
      </Drawer>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 2, md: 3 },
          pb: { xs: 10, md: 3 }, // Extra padding for bottom nav on mobile
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          mt: "80px",
          transition: `all ${tokens.animation.durationNormal} ${tokens.animation.easeInOut}`,
        }}
      >
        <AnimatePresence mode="wait">
          <motion.div
            key={location.pathname}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2, ease: [0.4, 0, 0.2, 1] }}
          >
            {children || <Outlet />}
          </motion.div>
        </AnimatePresence>
      </Box>

      {/* Mobile Bottom Navigation */}
      {isMobile && (
        <Paper
          sx={{
            position: "fixed",
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: theme.zIndex.drawer + 2,
            borderTop: "1px solid",
            borderColor: "divider",
            bgcolor: "background.paper",
            backdropFilter: "blur(10px)",
          }}
          elevation={8}
        >
          <BottomNavigation
            value={bottomNavValue}
            onChange={handleBottomNavChange}
            showLabels
            sx={{
              height: 64,
              bgcolor: "transparent",
              "& .MuiBottomNavigationAction-root": {
                minWidth: "auto",
                py: 1,
                color: "text.secondary",
                transition: `all ${tokens.animation.durationFast} ${tokens.animation.easeInOut}`,
                "&.Mui-selected": {
                  color: "primary.main",
                },
              },
              "& .MuiBottomNavigationAction-label": {
                fontSize: "0.7rem",
                fontWeight: 500,
                "&.Mui-selected": {
                  fontSize: "0.7rem",
                  fontWeight: 600,
                },
              },
            }}
          >
            {bottomNavItems.map((item, index) => (
              <BottomNavigationAction
                key={item.id}
                label={item.label}
                icon={
                  item.primary ? (
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: "50%",
                        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "white",
                        boxShadow: `0 4px 12px ${alpha(
                          theme.palette.primary.main,
                          0.4,
                        )}`,
                        transform: "translateY(-12px)",
                      }}
                    >
                      {item.icon}
                    </Box>
                  ) : (
                    item.icon
                  )
                }
                sx={
                  item.primary
                    ? { "& .MuiBottomNavigationAction-label": { mt: -1 } }
                    : {}
                }
              />
            ))}
          </BottomNavigation>
        </Paper>
      )}
    </Box>
  );
}
